import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, PlayCircle, FileText, Headphones, PenLine, CheckCircle2, Circle, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AiAssistant } from "../components/AiAssistant";
import { ExerciseBlock } from "../components/ExerciseBlock";
import { startCheckout } from "../components/PricingSection";
import { Progress } from "../components/ui/progress";

const TYPE_ICONS = { video: PlayCircle, text: FileText, audio: Headphones, exercise: PenLine };

export default function CoursePlayer() {
  const { courseId } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState({});
  const [showAi, setShowAi] = useState(false);
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  const [downloaded, setDownloaded] = useState(localStorage.getItem(`chimen_dl_${courseId}`) === "1");
  const [downloading, setDownloading] = useState(false);

  const downloadCourse = async () => {
    setDownloading(true);
    try {
      await fetch(`${API}/courses/${courseId}`, { credentials: "include" });
      if (course.image?.startsWith("/")) await fetch(course.image);
      for (const m of course.modules) {
        if (m.media_url) await fetch(`${API}${m.media_url.replace("/api", "")}`, { credentials: "include" });
      }
      localStorage.setItem(`chimen_dl_${courseId}`, "1");
      setDownloaded(true);
      toast.success("Séance disponible hors ligne — les modules restent lisibles sans connexion");
    } catch {
      toast.error("Téléchargement impossible — vérifiez votre connexion");
    } finally {
      setDownloading(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/courses/${courseId}`).then(({ data }) => setCourse(data)).catch(() => {});
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) return;
    api.get("/progress").then(({ data }) => {
      const map = {};
      data.filter((p) => p.course_id === courseId && p.completed).forEach((p) => { map[p.module_id] = true; });
      setProgress(map);
    }).catch(() => {});
  }, [user, courseId]);

  useEffect(() => {
    const lang = i18n.language;
    if (!course || lang === "fr" || !user) return;
    const m = course.modules[active];
    if (!m || m.locked || !m.content) return;
    const key = `${m.module_id}-${lang}`;
    if (translations[key]) return;
    setTranslating(true);
    api.post("/translate", { course_id: courseId, module_id: m.module_id, lang })
      .then(({ data }) => setTranslations((tr) => ({ ...tr, [key]: data })))
      .catch(() => {})
      .finally(() => setTranslating(false));
  }, [active, i18n.language, course, user, courseId, translations]);

  if (!course) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">{t("common.loading")}</div>;

  const mod = course.modules[active];
  const trMod = translations[`${mod.module_id}-${i18n.language}`];
  const displayTitle = trMod?.title || mod.title;
  const displayContent = trMod?.content || mod.content;
  const doneCount = course.modules.filter((m) => progress[m.module_id]).length;
  const pct = Math.round((doneCount / course.modules.length) * 100);

  const toggleDone = async () => {
    if (!user) { navigate("/auth"); return; }
    const val = !progress[mod.module_id];
    setProgress((p) => ({ ...p, [mod.module_id]: val }));
    await api.post("/progress", { course_id: courseId, module_id: mod.module_id, completed: val });
  };

  return (
    <main className="max-w-[1500px] mx-auto px-4 lg:px-6 py-8 relative z-10" data-testid="course-player">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-3 bg-card border border-primary/15 rounded-lg p-5 h-fit lg:sticky lg:top-20" data-testid="course-sidebar">
          <p className="text-[10px] uppercase tracking-[0.25em] text-primary">Niveau {course.level_id} · Cours {course.order}</p>
          <h1 className="font-serif-display text-2xl mt-2 leading-tight">{course.title}</h1>
          {user && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>{t("course.progress")}</span><span data-testid="course-progress-pct">{pct}%</span>
              </div>
              <Progress value={pct} className="h-1.5" />
            </div>
          )}
          <button onClick={downloadCourse} disabled={downloading || downloaded}
            className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs transition-colors duration-200 ${downloaded ? "bg-primary/10 text-primary border border-primary/30" : "border border-primary/30 text-foreground/70 hover:bg-primary/10"} disabled:opacity-70`}
            data-testid="download-course-btn">
            <Download size={13} />
            {downloaded ? "Disponible hors ligne ✓" : downloading ? "Téléchargement…" : "Télécharger cette séance"}
          </button>
          <div className="mt-6 space-y-1">
            {course.modules.map((m, i) => {
              const Icon = TYPE_ICONS[m.type] || FileText;
              return (
                <button key={m.module_id} onClick={() => setActive(i)}
                  className={`w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${i === active ? "bg-primary/10 text-foreground border border-primary/25" : "text-foreground/65 hover:bg-primary/5 border border-transparent"}`}
                  data-testid={`module-item-${m.module_id}`}>
                  {progress[m.module_id] ? <CheckCircle2 size={15} className="text-primary shrink-0" /> : m.locked ? <Lock size={14} className="text-muted-foreground shrink-0" /> : <Circle size={14} className="text-muted-foreground/50 shrink-0" />}
                  <span className="flex-1 leading-snug">{m.title}</span>
                  <Icon size={14} strokeWidth={1.5} className="text-primary/60 shrink-0" />
                </button>
              );
            })}
          </div>
          {!course.has_access && (
            <div className="mt-6 pt-5 border-t border-primary/10 space-y-2.5" data-testid="paywall-box">
              <p className="text-xs text-muted-foreground leading-relaxed">{t("course.lockedDesc")}</p>
              <button onClick={() => startCheckout("course_unit", courseId, user, navigate)}
                className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="buy-course-btn">
                {t("course.buyCourse")}
              </button>
              <button onClick={() => startCheckout("level_1_full", null, user, navigate)}
                className="w-full py-2.5 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200" data-testid="buy-level-btn">
                {t("course.buyLevel")}
              </button>
              <button onClick={() => startCheckout("subscription_monthly", null, user, navigate)}
                className="w-full py-2.5 rounded-full border border-primary/20 text-foreground/70 text-xs hover:bg-primary/5 transition-colors duration-200" data-testid="subscribe-btn">
                {t("course.subscribe")}
              </button>
            </div>
          )}
        </aside>

        {/* Content */}
        <section className={`${showAi ? "lg:col-span-6" : "lg:col-span-9"} bg-card border border-primary/15 rounded-lg p-8 lg:p-10 min-h-[70vh]`} data-testid="module-content">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">{mod.type} · {mod.duration} {mod.free && <span className="text-primary">· {t("course.free")}</span>}
                {translating && <span className="text-secondary normal-case tracking-normal"> · Traduction en cours…</span>}
              </p>
              <h2 className="font-serif-display text-3xl mt-2">{displayTitle}</h2>
            </div>
            <button onClick={() => setShowAi(!showAi)}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 text-primary text-xs hover:bg-primary/10 transition-colors duration-200"
              data-testid="toggle-ai-btn">
              <Sparkles size={14} /> {t("course.assistant")}
            </button>
          </div>
          <div className="gold-line my-7" />

          {mod.locked ? (
            <div className="text-center py-20" data-testid="locked-content">
              <Lock size={32} strokeWidth={1.2} className="mx-auto text-primary/50" />
              <h3 className="font-serif-display text-2xl mt-5">{t("course.locked")}</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">{t("course.lockedDesc")}</p>
              <button onClick={() => startCheckout("course_unit", courseId, user, navigate)}
                className="mt-8 px-7 py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200"
                data-testid="locked-buy-btn">
                {t("course.buyCourse")}
              </button>
            </div>
          ) : (
            <>
              {mod.type === "video" && (
                mod.media_url ? (
                  <video controls className="w-full aspect-video bg-black rounded-lg mb-8" src={`${API}${mod.media_url.replace("/api", "")}`} data-testid="video-player" />
                ) : (
                  <div className="aspect-video bg-background border border-primary/15 rounded-lg flex flex-col items-center justify-center mb-8" data-testid="video-placeholder">
                    <PlayCircle size={48} strokeWidth={1} className="text-primary/60" />
                    <p className="text-xs text-muted-foreground mt-3">Vidéo — contenu fourni par la Fondation</p>
                  </div>
                )
              )}
              {mod.type === "audio" && (
                mod.media_url ? (
                  <audio controls className="w-full mb-8" src={`${API}${mod.media_url.replace("/api", "")}`} data-testid="audio-player" />
                ) : (
                  <div className="bg-background border border-primary/15 rounded-lg p-6 flex items-center gap-4 mb-8" data-testid="audio-placeholder">
                    <Headphones size={28} strokeWidth={1.2} className="text-primary/60" />
                    <p className="text-xs text-muted-foreground">Audio — contenu fourni par la Fondation</p>
                  </div>
                )
              )}
              {mod.media_url && mod.media_content_type === "application/pdf" && (
                <a href={`${API}${mod.media_url.replace("/api", "")}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full border border-primary/40 text-primary text-sm hover:bg-primary/10 transition-colors duration-200" data-testid="pdf-download-link">
                  <FileText size={15} /> Télécharger le document PDF
                </a>
              )}
              {mod.type === "exercise" ? (
                <ExerciseBlock content={displayContent} courseId={courseId} />
              ) : (
                <div className="prose-content text-foreground/80 leading-[1.85] whitespace-pre-wrap text-base">{displayContent}</div>
              )}
              <div className="mt-12 pt-6 border-t border-primary/10 flex items-center justify-between">
                <button onClick={toggleDone}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-colors duration-200 ${progress[mod.module_id] ? "bg-primary/15 text-primary border border-primary/40" : "border border-primary/30 text-foreground/70 hover:bg-primary/10"}`}
                  data-testid="mark-done-btn">
                  <CheckCircle2 size={15} /> {progress[mod.module_id] ? t("course.done") : t("course.markDone")}
                </button>
                {active < course.modules.length - 1 && (
                  <button onClick={() => setActive(active + 1)} className="text-sm text-primary hover:underline underline-offset-4" data-testid="next-module-btn">
                    {course.modules[active + 1].title} →
                  </button>
                )}
              </div>
            </>
          )}
        </section>

        {/* AI panel */}
        {showAi && (
          <aside className="lg:col-span-3 rounded-lg overflow-hidden h-[70vh] lg:sticky lg:top-20">
            <AiAssistant courseId={courseId} moduleTitle={mod.title} onClose={() => setShowAi(false)} />
          </aside>
        )}
      </div>
    </main>
  );
}
